import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import WithdrawalSettlementPanel from "views/admin/Admissions/AddAdmissions/WithdrawalSettlementPanel";
import {
  fetchWithdrawalSettlement,
  saveWithdrawalSettlement,
} from "services/withdrawalSettlementApi";
import { canAdjustAgencyFinancials } from "utils/permissionUtils";

const EMPTY = {
  withdrawalTermUuid: null,
  settlementMode: "COLLECT_FULL",
  settledAgreed: "",
  retention: [],
  note: "",
};

/**
 * The accounts side of a withdrawal, opened from the Direct Account editor.
 *
 * The admissions user who withdrew picked the term and closed the student's balance; this is where
 * that is overridden if it was wrong, and where the commission and bonus on the settled and
 * cancelled terms are decided. Saving notifies the other accounts and super-admin users.
 */
const WithdrawalSettlementDialog = ({ open, onClose, admissionId, courseLabel, onSaved }) => {
  const [terms, setTerms] = useState([]);
  const [value, setValue] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const canAdjust = canAdjustAgencyFinancials();

  useEffect(() => {
    if (!open || !admissionId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchWithdrawalSettlement(admissionId)
      .then((data) => {
        if (cancelled) return;
        const row = data?.accountRows?.[0];
        const rowTerms = row?.terms || [];
        setTerms(rowTerms);
        setValue({
          ...EMPTY,
          withdrawalTermUuid: row?.settlementTermUuid ?? rowTerms[0]?.termUuid ?? null,
          settlementMode: row?.settlementMode ?? "COLLECT_FULL",
          settledAgreed: row?.settledAgreed ?? "",
          // Start from what is already stored, so saving a mode change does not read as
          // "retain nothing" for terms the team already decided to keep.
          retention: rowTerms
            .filter((term) => term.commissionRetained || term.bonusRetained)
            .map((term) => ({
              termUuid: term.termUuid,
              retainCommission: Boolean(term.commissionRetained),
              retainBonus: Boolean(term.bonusRetained),
            })),
        });
      })
      .catch(() => {
        if (!cancelled) setTerms([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, admissionId]);

  const handleSave = async () => {
    if (!value.withdrawalTermUuid) {
      toast.warning("Pick the term the student withdrew during.");
      return;
    }
    setSaving(true);
    try {
      const saved = await saveWithdrawalSettlement(admissionId, value);
      toast.success("Settlement saved. The accounts team has been notified.");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to save the settlement."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>
        Withdrawal settlement
        {courseLabel ? ` — ${courseLabel}` : ""}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6">
            <CircularProgress size={18} />
            <span className="text-sm text-app-text-muted">
              Loading this admission&apos;s terms…
            </span>
          </div>
        ) : terms.length === 0 ? (
          <Alert severity="info">This admission has no term schedule to settle.</Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This application is withdrawn. Override how the settled term is closed, and decide
              which commission and bonus still count.
            </Alert>
            <WithdrawalSettlementPanel
              terms={terms}
              value={value}
              onChange={setValue}
              readOnly={!canAdjust}
              canAdjustFinancials={canAdjust}
            />
            {canAdjust && (
              <TextField
                fullWidth
                size="small"
                label="Note (optional)"
                placeholder="Why this was settled the way it was"
                value={value.note ?? ""}
                onChange={(e) => setValue((prev) => ({ ...prev, note: e.target.value }))}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={saving}>
          {canAdjust ? "Cancel" : "Close"}
        </Button>
        {canAdjust && terms.length > 0 && (
          <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save settlement"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalSettlementDialog;
